import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyApi } from 'features/company/company.api';
import { useCompany } from 'features/company/CompanyContext';
import { EmptyState, ErrorState, PageHeader, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { Pagination } from 'shared/components/Pagination';
import { formatDateTime } from 'shared/utilities/format';

export function CompanyParticipantsPage() {
  const { t, locale } = useI18n();
  const { companyId } = useCompany();
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: queryKeys.companyParticipants(page),
    queryFn: () => companyApi.participants(companyId, { page, per_page: 15 }),
  });

  if (query.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const items = query.data?.items || [];
  return (
    <div className="company-participants-page">
      <PageMeta titleKey="meta.companyParticipants" />
      <PageHeader title={t('company.participants.title')} description={t('company.participants.body')} />
      {items.length === 0 ? (
        <EmptyState title={t('company.participants.empty')} />
      ) : (
        <div className="record-table">
          {items.map((participant) => (
            <article key={participant.attempt_id || participant.access_code_id} className="record-row">
              <div>
                <strong>{participant.full_name}</strong>
                <StatusChip>{participant.attempt_status_label || participant.attempt_status}</StatusChip>
              </div>
              <p dir="ltr">{participant.code_masked}</p>
              <p>{participant.batch_label || '—'}</p>
              <p className="muted">{participant.claimed_at ? formatDateTime(participant.claimed_at, locale) : '—'}</p>
              <p className="muted">{participant.completed_at ? formatDateTime(participant.completed_at, locale) : '—'}</p>
              {participant.attempt_status === 'completed' && participant.attempt_id ? (
                <Link className="btn btn-secondary" to={paths.companyParticipantResult(participant.attempt_id)}>
                  {t('actions.viewResult')}
                </Link>
              ) : <span>{participant.profile_code || '—'}</span>}
            </article>
          ))}
        </div>
      )}
      <Pagination meta={query.data?.meta} page={page} onPageChange={setPage} />
    </div>
  );
}
